import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/api/client";
import { Loader2, ShieldCheck } from "lucide-react";

export default function Login() {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const u = await login(email.trim(), password);
            toast.success("Welcome back");
            navigate(u?.role === "super_admin" ? "/platform" : "/dashboard");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50">
            <div className="hidden lg:flex flex-col justify-between p-12 bg-primary text-white relative overflow-hidden">
                <div className="absolute inset-0 grid-noise opacity-20 pointer-events-none" />
                <div className="relative">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-white/15 backdrop-blur grid place-items-center font-display font-bold text-lg">
                            C
                        </div>
                        <div>
                            <div className="font-display text-xl font-bold">CAFlow</div>
                            <div className="text-xs uppercase tracking-widest text-white/70">
                                Practice OS for Chartered Accountants
                            </div>
                        </div>
                    </div>
                </div>
                <div className="relative">
                    <h2 className="font-display text-4xl font-bold leading-tight max-w-md">
                        Every GST, TDS &amp; ITR deadline, in one calm workspace.
                    </h2>
                    <p className="mt-4 text-white/80 max-w-md leading-relaxed">
                        Track clients, assign work, and never miss a compliance date —
                        purpose-built for small &amp; mid-size CA firms in India.
                    </p>
                    <div className="mt-8 grid grid-cols-3 gap-6 max-w-md">
                        {[
                            ["120+", "Compliance types"],
                            ["24/7", "Deadline monitor"],
                            ["100%", "Data isolation"],
                        ].map(([n, l]) => (
                            <div key={l}>
                                <div className="font-display text-2xl font-bold">{n}</div>
                                <div className="text-xs text-white/70 uppercase tracking-wider">
                                    {l}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="relative text-xs text-white/60 flex items-center gap-2">
                    <ShieldCheck size={14} /> JWT + bcrypt · Role-based access · Audit
                    logs
                </div>
            </div>

            <div className="flex items-center justify-center p-6 sm:p-12">
                <div className="w-full max-w-md">
                    <div className="lg:hidden mb-6 flex items-center gap-2">
                        <div className="w-9 h-9 rounded-lg bg-primary text-white grid place-items-center font-display font-bold">
                            C
                        </div>
                        <span className="font-display text-lg font-bold">CAFlow</span>
                    </div>
                    <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">
                        Sign in
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Manage your firm from anywhere.
                    </p>

                    <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
                        <div className="space-y-1.5">
                            <Label htmlFor="email">Work email</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="you@firm.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoComplete="email"
                                data-testid="login-email"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="password">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                placeholder="••••••••"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                autoComplete="current-password"
                                data-testid="login-password"
                            />
                        </div>
                        <Button
                            type="submit"
                            className="w-full bg-primary hover:bg-primary/90"
                            disabled={busy}
                            data-testid="login-submit"
                        >
                            {busy ? <Loader2 className="animate-spin" size={16} /> : "Sign in"}
                        </Button>
                    </form>

                    <div className="mt-6 text-sm text-slate-500 text-center">
                        New firm?{" "}
                        <Link to="/register" className="text-primary font-medium hover:underline">
                            Create a workspace
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
